import React, { useState } from 'react';
import { XIcon, CodeIcon, CheckIcon, Sparkles, Building2, TrendingUp, ChevronDown } from 'lucide-react';
import { Button } from './ui/Button';
import { DraggableModal } from './ui/DraggableModal';
import { mockGLCodingData } from '../data/mockGLData';
import { SelectedGLDimensions } from '../types/glCoding';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
interface BulkGLCodingModalProps {
  lineItemCount: number;
  vendor?: string;
  onApply: (glCode: string, dimensions: SelectedGLDimensions) => void;
  onClose: () => void;
}
// Mock vendor defaults - aligned with available dimensions
const mockVendorDefaults: SelectedGLDimensions = {
  Department: '200',
  'Cost Centre': '2010',
  Project: 'BAU',
  Activity: 'A800',
  Region: 'GLOBAL'
};
// Mock top used GL codes
const mockTopUsed = [{
  glCode: '100-1010-P001-A200-GLOBAL',
  useCount: 12,
  dimensions: {
    Department: '100',
    'Cost Centre': '1010',
    Project: 'P001',
    Activity: 'A200',
    Region: 'GLOBAL'
  }
}, {
  glCode: '400-4010-P006-A400-GLOBAL',
  useCount: 8,
  dimensions: {
    Department: '400',
    'Cost Centre': '4010',
    Project: 'P006',
    Activity: 'A400',
    Region: 'GLOBAL'
  }
}, {
  glCode: '300-3010-P005-A600-NA-US',
  useCount: 5,
  dimensions: {
    Department: '300',
    'Cost Centre': '3010',
    Project: 'P005',
    Activity: 'A600',
    Region: 'NA-US'
  }
}];
const mockVendor = {
  name: 'Office Supplies Co',
  category: 'Office & Stationery'
};
export function BulkGLCodingModal({
  lineItemCount,
  vendor = 'Office Supplies Co',
  onApply,
  onClose
}: BulkGLCodingModalProps) {
  const [selectedDimensions, setSelectedDimensions] = useState<SelectedGLDimensions>({});
  const [searchTerms, setSearchTerms] = useState<Record<string, string>>({});
  const [showSuggestions, setShowSuggestions] = useState(true);
  // Keyboard shortcuts
  useKeyboardShortcuts({
    shortcuts: [{
      key: 'Escape',
      handler: onClose,
      description: 'Close modal'
    }],
    enabled: true
  });
  const dimensionOrder = ['Department', 'Cost Centre', 'Project', 'Activity', 'Region'];
  const handleDimensionSelect = (dimensionName: string, code: string) => {
    setSelectedDimensions(prev => ({
      ...prev,
      [dimensionName]: code
    }));
  };
  const handleSearchChange = (dimensionName: string, value: string) => {
    setSearchTerms(prev => ({
      ...prev,
      [dimensionName]: value
    }));
  };
  const handleLoadSuggestion = (dimensions: SelectedGLDimensions) => {
    setSelectedDimensions(dimensions);
  };
  const getFilteredOptions = (dimensionName: string) => {
    const options = mockGLCodingData.dimensions[dimensionName] || [];
    const searchTerm = searchTerms[dimensionName]?.toLowerCase() || '';
    if (!searchTerm) return options;
    return options.filter(option => option.code.toLowerCase().includes(searchTerm) || option.description.toLowerCase().includes(searchTerm));
  };
  const buildGLCode = () => {
    const parts = dimensionOrder.map(dim => selectedDimensions[dim] || '0000');
    return parts.join(mockGLCodingData.separator);
  };
  const glCode = buildGLCode();
  const isComplete = dimensionOrder.every(dim => selectedDimensions[dim]);
  const handleApply = () => {
    if (isComplete) {
      onApply(glCode, selectedDimensions);
    }
  };
  return <DraggableModal isOpen={true} onClose={onClose} title="Apply GL Code to All Line Items" defaultWidth={1400} defaultHeight={800} minWidth={1000} minHeight={600} data-id="element-2">
      <div className="flex flex-col h-full" data-id="element-3">
        {/* Header Info */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50 flex-shrink-0" data-id="element-4">
          <div className="flex items-center gap-2" data-id="element-5">
            <CodeIcon className="w-5 h-5 text-blue-600" data-id="element-6" />
            <p className="text-sm text-gray-600" data-id="element-7">
              This GL code will be applied to all {lineItemCount} line items
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-8">
            <XIcon className="w-5 h-5 text-gray-500" data-id="element-9" />
          </button>
        </div>

        {/* GL Code Preview */}
        <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100 flex-shrink-0" data-id="element-10">
          <div className="flex items-center justify-between" data-id="element-11">
            <div data-id="element-12">
              <div className="text-xs font-medium text-blue-700 mb-1" data-id="element-13">
                Generated GL Code
              </div>
              <code className="text-2xl font-mono font-bold text-blue-900" data-id="element-14">
                {glCode}
              </code>
            </div>
            {isComplete && <div className="flex items-center gap-2 text-green-700" data-id="element-15">
                <CheckIcon className="w-5 h-5" data-id="element-16" />
                <span className="text-sm font-medium" data-id="element-17">Complete</span>
              </div>}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto" data-id="element-18">
          <div className="p-6 space-y-6" data-id="element-19">
            {/* Smart Suggestions Section */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-4 border-2 border-purple-200" data-id="element-20">
              <button onClick={() => setShowSuggestions(!showSuggestions)} className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:text-gray-700 transition-colors w-full" data-id="element-21">
                <Sparkles size={18} className="text-purple-600" data-id="element-22" />
                Quick Apply from Previous Coding
                <span className="text-xs text-gray-600 ml-auto mr-2" data-id="element-23">
                  Click to use
                </span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showSuggestions ? 'rotate-180' : ''}`} data-id="element-24" />
              </button>

              {showSuggestions && <div className="mt-4 space-y-4" data-id="element-25">
                  {/* Vendor Default */}
                  <div data-id="element-26">
                    <div className="flex items-center gap-1.5 mb-2" data-id="element-27">
                      <Building2 size={14} className="text-teal-600" data-id="element-28" />
                      <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-29">
                        Vendor Default Configuration
                      </h4>
                    </div>
                    <button onClick={() => handleLoadSuggestion(mockVendorDefaults)} className="w-full text-left px-4 py-3 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-lg border-2 border-teal-300 hover:border-teal-500 hover:shadow-md transition-all group" data-id="element-30">
                      <div className="flex items-start justify-between gap-3" data-id="element-31">
                        <div className="flex-1" data-id="element-32">
                          <div className="flex items-center gap-2 mb-1" data-id="element-33">
                            <div className="text-sm font-bold text-teal-900 group-hover:text-teal-700" data-id="element-34">
                              {vendor}
                            </div>
                            <span className="px-2 py-0.5 bg-teal-200 text-teal-800 text-xs font-medium rounded" data-id="element-35">
                              {mockVendor.category}
                            </span>
                          </div>
                          <div className="text-xs text-teal-700 mb-2" data-id="element-36">
                            Standard office supplies and stationery purchases
                          </div>
                          <div className="flex flex-wrap gap-1.5" data-id="element-37">
                            {dimensionOrder.map(dimensionName => {
                          const code = mockVendorDefaults[dimensionName];
                          if (!code) return null;
                          return <span key={dimensionName} className="px-2 py-0.5 bg-white text-teal-900 text-xs rounded border border-teal-200" data-id="element-38">
                                  {dimensionName}: {code}
                                </span>;
                        })}
                          </div>
                        </div>
                        <Sparkles size={18} className="text-teal-500 group-hover:text-teal-600 flex-shrink-0" data-id="element-39" />
                      </div>
                    </button>
                  </div>

                  {/* Top 3 Used */}
                  <div data-id="element-40">
                    <div className="flex items-center gap-1.5 mb-2" data-id="element-41">
                      <TrendingUp size={14} className="text-green-600" data-id="element-42" />
                      <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-43">
                        Top 3 Most Used GL Codes
                      </h4>
                    </div>
                    <div className="space-y-2" data-id="element-44">
                      {mockTopUsed.map((item, index) => <button key={item.glCode} onClick={() => handleLoadSuggestion(item.dimensions)} className="w-full text-left px-4 py-3 bg-white rounded-lg border-2 border-green-200 hover:border-green-400 hover:bg-green-50 transition-all group" data-id="element-45">
                          <div className="flex items-center justify-between" data-id="element-46">
                            <div className="flex items-center gap-3 flex-1 min-w-0" data-id="element-47">
                              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-100 text-green-700 font-bold text-sm flex-shrink-0" data-id="element-48">
                                #{index + 1}
                              </div>
                              <div className="flex-1 min-w-0" data-id="element-49">
                                <code className="text-sm font-mono font-bold text-gray-900 group-hover:text-green-700 block" data-id="element-50">
                                  {item.glCode}
                                </code>
                                <div className="flex flex-wrap gap-1 mt-1" data-id="element-51">
                                  {dimensionOrder.map(dimensionName => {
                              const code = item.dimensions[dimensionName];
                              if (!code) return null;
                              return <span key={dimensionName} className="text-xs text-gray-600" data-id="element-52">
                                        {dimensionName}: {code}
                                      </span>;
                            })}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0" data-id="element-53">
                              <span className="text-xs text-gray-500" data-id="element-54">
                                Used {item.useCount}x
                              </span>
                              <Sparkles size={16} className="text-green-400 group-hover:text-green-600" data-id="element-55" />
                            </div>
                          </div>
                        </button>)}
                    </div>
                  </div>
                </div>}
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3" data-id="element-56">
              <div className="flex-1 border-t border-gray-300" data-id="element-57"></div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-58">
                Or build manually
              </span>
              <div className="flex-1 border-t border-gray-300" data-id="element-59"></div>
            </div>

            {/* Dimension Selection Columns */}
            <div className="grid grid-cols-5 gap-4" data-id="element-60">
              {dimensionOrder.map(dimensionName => {
              const options = getFilteredOptions(dimensionName);
              const selectedCode = selectedDimensions[dimensionName];
              return <div key={dimensionName} className="flex flex-col" data-id="element-61">
                    <div className="mb-3" data-id="element-62">
                      <label className="block text-sm font-semibold text-gray-900 mb-2" data-id="element-63">
                        {dimensionName}
                      </label>
                      <input type="text" placeholder="Search..." value={searchTerms[dimensionName] || ''} onChange={e => handleSearchChange(dimensionName, e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-64" />
                    </div>

                    <div className="flex-1 border border-gray-200 rounded-lg overflow-hidden" data-id="element-65">
                      <div className="max-h-80 overflow-y-auto" data-id="element-66">
                        {options.map(option => {
                      const isSelected = selectedCode === option.code;
                      return <button key={option.code} onClick={() => handleDimensionSelect(dimensionName, option.code)} className={`w-full text-left px-3 py-2.5 border-b border-gray-100 transition-colors ${isSelected ? 'bg-blue-50 border-l-4 border-l-blue-600' : 'hover:bg-gray-50'}`} data-id="element-67">
                              <div className="flex items-center justify-between" data-id="element-68">
                                <div className="flex-1 min-w-0" data-id="element-69">
                                  <div className={`text-sm font-mono font-semibold ${isSelected ? 'text-blue-900' : 'text-gray-900'}`} data-id="element-70">
                                    {option.code}
                                  </div>
                                  <div className={`text-xs mt-0.5 truncate ${isSelected ? 'text-blue-700' : 'text-gray-600'}`} title={option.description} data-id="element-71">
                                    {option.description}
                                  </div>
                                </div>
                                {isSelected && <CheckIcon className="w-4 h-4 text-blue-600 ml-2 flex-shrink-0" data-id="element-72" />}
                              </div>
                            </button>;
                    })}
                      </div>
                    </div>
                  </div>;
            })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50 flex-shrink-0" data-id="element-73">
          <div className="text-sm text-gray-600" data-id="element-74">
            {isComplete ? <span className="text-green-700 font-medium" data-id="element-75">
                ✓ Ready to apply to {lineItemCount} line items
              </span> : <span data-id="element-76">Select all dimensions to complete the GL code</span>}
          </div>
          <div className="flex gap-3" data-id="element-77">
            <Button variant="primary" onClick={handleApply} disabled={!isComplete} data-id="element-78">
              Apply to All {lineItemCount} Lines
            </Button>
            <Button variant="ghost" onClick={onClose} data-id="element-79">
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </DraggableModal>;
}